import React from "react";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Copy } from "lucide-react";
import { BasicInfoSection } from "../LocationFormSections/BasicInfoSection";
import { AddressSection } from "../LocationFormSections/AddressSection";
import { DescriptionSection } from "../LocationFormSections/DescriptionSection";
import { AdditionalInfoSection } from "../LocationFormSections/AdditionalInfoSection";
import { CampExperienceSections } from "../LocationFormSections/CampExperienceSections";
import { AgeGroupsOfferingsSection } from "../LocationFormSections/AgeGroupsOfferingsSection";
import { BulkCopyConfirmationModal } from "./BulkCopyConfirmationModal";
import { useBulkCopyLocation } from "../hooks/useBulkCopyLocation";
import { useAuth } from "@/context/AuthContext";
import { CampLocation } from "@/types/campLocation";
import { UseFormReturn } from "react-hook-form";
import { LocationFormData } from "../schemas/locationFormSchema";

interface LocationFormContentProps {
  form: UseFormReturn<LocationFormData>;
  onSubmit: (values: LocationFormData) => Promise<void>;
  location: CampLocation;
  showAboutCampTab?: boolean;
  hideAgeGroups?: boolean;
  isWelcomeScreen?: boolean;
  onApplyLocationDescriptionToAll?: (description: string) => Promise<void>;
  applyLocationDescriptionToAllLoading?: boolean;
  onApplySearchResultExcerptToAll?: (excerpt: string) => Promise<void>;
  onApplyDescTimingAndBookingToAll?: (value: string) => Promise<void>;
  onApplyAdditionalInfoToAll?: (value: { doesWaitlist: boolean | null; doesScholarships: boolean | null; scholarshipNotes: string }) => Promise<void>;
  onApplyAllergyAccommodationToAll?: (value: { allergyKeys: string[]; allergyNotes: string }) => Promise<void>;
  onApplyReligiousAffiliationToAll?: (value: { religiousAffiliationKeys: string[]; religiousAffiliationNotes: string }) => Promise<void>;
  onApplySupportForToAll?: (value: { supportForKeys: string[]; supportForNotes: string }) => Promise<void>;
  applyOrgFieldsToAllLoading?: boolean;
  onPublicSlugSaved?: () => void;
}

export const LocationFormContent: React.FC<LocationFormContentProps> = ({
  form,
  onSubmit,
  location,
  showAboutCampTab = false,
  hideAgeGroups = false,
  isWelcomeScreen = false,
  onApplyLocationDescriptionToAll,
  applyLocationDescriptionToAllLoading = false,
  onApplySearchResultExcerptToAll,
  onApplyDescTimingAndBookingToAll,
  onApplyAdditionalInfoToAll,
  onApplyAllergyAccommodationToAll,
  onApplyReligiousAffiliationToAll,
  onApplySupportForToAll,
  applyOrgFieldsToAllLoading = false,
  onPublicSlugSaved,
}) => {
  const { isAdmin } = useAuth();
  const {
    isModalOpen,
    isLoading,
    targetLocations,
    showBulkCopyModal,
    closeBulkCopyModal,
    executeBulkCopy
  } = useBulkCopyLocation({ location });

  return (
    <Form {...form}>
      <form id="location-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        {showAboutCampTab ? (
          <div>
            <p>This tab has been replaced by fields in the Details tab.</p>
          </div>
        ) : (
          <>
            <BasicInfoSection
              form={form}
              location={location}
              isWelcomeScreen={isWelcomeScreen}
              onApplySearchResultExcerptToAll={onApplySearchResultExcerptToAll}
              applySearchResultExcerptToAllLoading={applyOrgFieldsToAllLoading}
              onPublicSlugSaved={onPublicSlugSaved}
            />
            <AddressSection form={form} location={location} isWelcomeScreen={isWelcomeScreen} />
            <DescriptionSection
              form={form}
              onApplyLocationDescriptionToAll={onApplyLocationDescriptionToAll}
              onApplyDescTimingAndBookingToAll={onApplyDescTimingAndBookingToAll}
              applyToAllLoading={applyLocationDescriptionToAllLoading}
            />
            
            {/* Admin-only bulk copy button */}
            {isAdmin && !isWelcomeScreen && (
              <div className="flex justify-center py-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={showBulkCopyModal}
                  disabled={isLoading}
                  className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 hover:border-blue-300"
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Copy everything below to ALL locations for this org.
                </Button>
              </div>
            )}
            
            <AdditionalInfoSection
              form={form}
              onApplyAdditionalInfoToAll={onApplyAdditionalInfoToAll}
              applyToAllLoading={applyOrgFieldsToAllLoading}
            />
            <CampExperienceSections
              form={form}
              onApplyAllergyAccommodationToAll={onApplyAllergyAccommodationToAll}
              onApplyReligiousAffiliationToAll={onApplyReligiousAffiliationToAll}
              onApplySupportForToAll={onApplySupportForToAll}
              applyToAllLoading={applyOrgFieldsToAllLoading}
            />
            {!hideAgeGroups && <AgeGroupsOfferingsSection location={location} />}
          </>
        )}
      </form>
      
      {/* Bulk copy confirmation modal */}
      {isAdmin && (
        <BulkCopyConfirmationModal
          isOpen={isModalOpen}
          onClose={closeBulkCopyModal}
          onConfirm={executeBulkCopy}
          targetLocations={targetLocations}
          sourceLocationName={location.name || "Unknown Location"}
          isLoading={isLoading}
        />
      )}
    </Form>
  );
};
